import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useApp } from '../../AppContext';

const MobileNav = styled.nav`
  width: 100%;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  backdrop-filter: blur(15px);
  z-index: 15000;
  overflow: visible;
`;

const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
`;

const Logo = styled.div`
  color: #fff;
  font-size: 1.2rem;
  font-weight: 600;
  text-decoration: none;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 4px;

  span {
    display: block;
    width: 25px;
    height: 3px;
    background-color: white;
  }
`;

/* 헤더 아래에서 열리는 좌측 드로어. 헤더 높이만큼 아래에서 시작 */
const HEADER_H = 64;

const Overlay = styled.div`
  position: fixed;
  top: ${HEADER_H}px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 14998;
  opacity: ${props => (props.isOpen ? 1 : 0)};
  visibility: ${props => (props.isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.28s ease, visibility 0.28s ease;
`;

/* 좌측에서 슬라이드되는 드로어 (헤더 아래) */
const Drawer = styled.aside`
  position: fixed;
  top: ${HEADER_H}px;
  left: 0;
  bottom: 0;
  width: 82%;
  max-width: 300px;
  background: #ffffff;
  z-index: 14999;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 16px rgba(0, 0, 0, 0.18);
  transform: translateX(${props => (props.isOpen ? '0' : '-100%')});
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const MenuItem = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.2s ease;

  div {
    color: #333;
    text-decoration: none;
    font-size: 1rem;
    font-weight: 500;
    display: block;
  }

  &:hover { background: #f8f9fa; div { color: #007bff; } }
  &:last-child { border-bottom: none; }
`;

const UserInfo = styled.div`
  padding: 1.25rem 1.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;

  span { color: #495057; font-weight: 500; font-size: 0.95rem; }
`;

const LogoutButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  transition: background 0.2s ease;
  &:hover { background: #c82333; }
`;

const MobileNavigator = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const { user, logout } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const response = await fetch('/request/getMenuItems');
        const data = await response.json();
        setMenuItems(data);
      } catch (error) {
        console.error('Error fetching menu items:', error);
      }
    };
    fetchMenuItems();
  }, []);

  // 드로어 열림 시 본문 스크롤 잠금
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const toggleMenu = () => setIsMenuOpen((v) => !v);
  const closeMenu = () => setIsMenuOpen(false);

  const handleLogout = async () => {
    try {
      const response = await fetch('/request/logout', { credentials: 'include', method: 'GET' });
      if (response.ok) {
        logout();
        sessionStorage.removeItem('user');
        navigate('/login');
      } else {
        console.error('Failed to logout');
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
    setIsMenuOpen(false);
  };

  const handleNavigate = (path) => {
    navigate(path, { state: { fromNavigator: true } });
    setIsMenuOpen(false);
  };

  return (
    <>
      <MobileNav>
        <MobileHeader>
          <div onClick={() => handleNavigate('/')} style={{ cursor: 'pointer' }}>
            <Logo>AntsNest</Logo>
          </div>
          <MenuButton onClick={toggleMenu} aria-label="메뉴">
            <span></span>
            <span></span>
            <span></span>
          </MenuButton>
        </MobileHeader>
      </MobileNav>

      {/* 드로어/오버레이는 헤더(backdrop-filter) 밖에 두어 뷰포트 기준으로 고정 */}
      <Overlay isOpen={isMenuOpen} onClick={closeMenu} />

      <Drawer isOpen={isMenuOpen}>
        <DrawerBody>
          {user && (
            <UserInfo>
              <span>{user.name || user.id} 님</span>
              <LogoutButton onClick={handleLogout}>로그아웃</LogoutButton>
            </UserInfo>
          )}
          {menuItems.map((item) => (
            <MenuItem key={item.std_id}>
              <div onClick={() => handleNavigate(`/${item.std_value}`)}>{item.std_key}</div>
            </MenuItem>
          ))}
        </DrawerBody>
      </Drawer>
    </>
  );
};

export default MobileNavigator;
